import { collectErrors } from "@formant/core/common/collectErrors";
import { delay } from "@formant/core/common/delay";
import { logger } from "@formant/core/common/logger";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import {
    IPromiseIntervalHandle,
    setPromiseInterval,
} from "@formant/core/common/setPromiseInterval";
import { stats } from "@formant/core/common/stats";
import { duration } from "@formant/core/model/duration";
import { IRtcPeer } from "@formant/core/model/IRtcPeer";
import { IRtcSession } from "@formant/core/model/IRtcSession";
import { IRtcSignal } from "@formant/core/model/IRtcSignal";
import { RtcConnectionStatus } from "@formant/core/model/RtcConnectionStatus";
import { Timestamp } from "@formant/core/model/Timestamp";
import { Uuid } from "@formant/core/model/Uuid";

import { IRtcClientConfigurationV1 } from "./models-v1/IRtcClientConfigurationV1";
import { IIceMode } from "./models/IIceMode";
import { IRtcSendConfiguration } from "./models/IRtcSendConfiguration";
import { IRtcStreamMessage } from "./models/IRtcStreamMessage";
import { IStreamControl } from "./models/IStreamControl";
import { RtcConnectionV1 } from "./RtcConnectionV1";
import { forceGarbageCollection } from "./utils/garbageCollect";
import { isInactive } from "./utils/isInactive";

/**
 * RtcClient to be used by browsers.
 * Interacts with signaling API v1.
 */
export class RtcClientV1 {
    private localPeer: Promise<IRtcPeer>;
    private updateInterval: IPromiseIntervalHandle;
    private connections: RtcConnectionV1[] = [];
    private connectingSessions = new Map<Uuid, Timestamp>();
    private rtcInstancesConstructed = 0;

    constructor(private config: IRtcClientConfigurationV1) {
        this.updateInterval = setPromiseInterval({
            func: async () => {
                try {
                    await this.update();
                } catch (error) {
                    logger.warn("RTC update failed", { error });
                }
            },
            delay: 250 * duration.millisecond,
        });
        this.localPeer = this.createPeer();
    }

    public send(
        peerId: Uuid,
        message: IRtcStreamMessage,
        config: IRtcSendConfiguration,
    ) {
        const connection = this.getActiveConnection(peerId);

        if (!connection) {
            logger.warn(
                "Attempted to send with no active connection to that peer.",
            );
            return;
        }

        if (!connection.isReady()) {
            logger.warn(
                "Attempted to send with an active connection to that peer which was not yet ready.",
            );
            return;
        }

        connection.send(message, config);
    }

    public controlRemoteStream(peerId: Uuid, streamControl: IStreamControl) {
        const connection = this.getActiveConnection(peerId);

        if (!connection) {
            logger.warn(
                "Attempted to control remote stream with no active connection to that peer.",
            );
            return;
        }

        if (!connection.isReady()) {
            logger.warn(
                "Attempted to control remote stream with an active connection to that peer which was not yet ready.",
            );
            return;
        }

        connection.controlRemoteStream(streamControl);
    }

    /**
     * Connects to a peer given its id.
     * Returns the id of the session, if one is created.
     */
    public async connect(peerId: Uuid): Promise<Uuid | undefined> {
        const localPeer = await this.getLocalPeer();

        // If there's already an active connection, do nothing and return.
        if (this.getActiveConnection(peerId)) {
            logger.warn(
                "Attempted to connect to peer with an already active connection.",
            );
            return;
        }

        // Otherwise, request a session be created.
        let session: IRtcSession | undefined;
        try {
            session = await this.config.signalingClient.createSession(
                await this.config.getToken(),
                {
                    offerPeerId: localPeer.id,
                    answerPeerId: peerId,
                },
            );
        } catch (error) {
            narrowError(error, ResponseError, _ => _.statusCode === 404);
            logger.warn("Attempted to connect to peer that does not exist.");
        }

        if (session) {
            this.connectingSessions.set(session.id, new Date().getTime());
            // this is where offer peers call connectToSession
            await this.connectToSession(session);

            // RtcConnection has a built-in timeout which will eventually return
            // "disconnected" here when no connection is established
            while (true) {
                await delay(0.1);
                switch (this.getConnectionStatus(peerId)) {
                    case "connecting":
                        continue;
                    case "connected":
                        return session.id;
                    case "disconnected":
                        return;
                }
            }
        }
    }

    public getConnectionStatus(peerId: Uuid): RtcConnectionStatus {
        const connection = this.getActiveConnection(peerId);
        if (!connection) {
            return "disconnected";
        }
        return connection.isReady() ? "connected" : "connecting";
    }

    public getIceMode(peerId: Uuid): IIceMode | undefined {
        const connection = this.getActiveConnection(peerId);
        if (!connection) {
            return;
        }
        return connection.iceMode;
    }

    public async disconnect(peerId: Uuid) {
        const connection = this.getActiveConnection(peerId);
        if (connection) {
            await this.closeConnection(connection);
        }
    }

    public async getLocalPeer(): Promise<IRtcPeer> {
        return await this.localPeer;
    }

    public async getPeers(): Promise<IRtcPeer[]> {
        return await this.config.signalingClient.getPeers(
            await this.config.getToken(),
        );
    }

    public getPing(peerId: Uuid): number | undefined {
        const connection = this.getActiveConnection(peerId);
        if (!connection) {
            logger.warn("Attempted to get ping time from inactive peer.");
            return;
        }
        return connection.getPing();
    }

    public getLastMessageTimestamp(remotePeerId: Uuid): Timestamp | undefined {
        const connection = this.getActiveConnection(remotePeerId);
        if (!connection) {
            logger.warn("Attempted to get ping time from inactive peer.");
            return;
        }
        return connection.getLastMessageTimestamp();
    }

    public async shutdown() {
        await this.updateInterval.stop();
        const connections = this.connections;
        this.connections = [];
        await this.closeConnections(connections);
    }

    private async closeConnection(connection: RtcConnectionV1) {
        this.connectingSessions.delete(connection.getSessionId());
        await connection.close();
        try {
            await this.config.signalingClient.deleteSession(
                await this.config.getToken(),
                connection.getSessionId(),
            );
        } catch (error) {
            // The session could have been expired by the signaling-api or
            // deleted by the device, in which case this will 404.
            narrowError(error, ResponseError, _ => _.statusCode === 404);
        }
    }

    private async getSessions() {
        const localPeer = await this.getLocalPeer();
        return (
            await this.config.signalingClient.getSessions(
                await this.config.getToken(),
            )
        ).filter(
            _ =>
                _.offer.peer.id === localPeer.id ||
                _.answer.peer.id === localPeer.id,
        );
    }

    private async createPeer() {
        return await this.config.signalingClient.createPeer(
            await this.config.getToken(),
            {
                capabilitySet: this.config.capabilitySet || {},
            },
        );
    }

    private async createRTCPeerConnection(config: RTCConfiguration = {}) {
        const RTCPeerConnectionClass =
            this.config.alternateRTCPeerConnection || RTCPeerConnection;

        this.rtcInstancesConstructed++;
        if (!(this.rtcInstancesConstructed % 20)) {
            forceGarbageCollection();
        }

        try {
            return new RTCPeerConnectionClass({
                iceServers: await this.config.signalingClient.getIceServers(
                    await this.config.getToken(),
                ),
                ...config,
            });
        } catch (err) {
            logger.debug(
                `Failed to create RTCPeerConnection: ${this.rtcInstancesConstructed} total allocated`,
            );
            throw err;
        }
    }

    private async closeConnections(connections: RtcConnectionV1[]) {
        const errors = await collectErrors(
            connections.map(async _ => {
                await this.closeConnection(_);
            }),
        );
        for (const error of errors) {
            logger.warn("Request to close session failed", { error });
        }
    }

    private async reset() {
        // the rest of the update loop is called synchronously after this call
        // we don't have to worry about this.connections or this.connectingSessions state mutating
        // due to reset in other methods, or in reset due to other methods
        await this.closeConnections(this.connections);
        this.connections = [];
        this.connectingSessions = new Map<Uuid, Timestamp>();
        this.localPeer = this.createPeer();
        await this.localPeer;
    }

    private getActiveConnection(peerId: Uuid) {
        return this.connections.find(
            _ => _.getRemotePeer().id === peerId && _.isActive(),
        );
    }

    private async update() {
        const localPeer = await this.getLocalPeer();
        try {
            await this.config.signalingClient.refreshPeer(
                await this.config.getToken(),
                localPeer.id,
            );
        } catch (error) {
            narrowError(error, ResponseError, _ => _.statusCode === 404);
            logger.warn("Peer expired, creating new peer", {
                peerId: localPeer.id,
            });
            await this.reset();
        }

        // Sync with the session state of the signaling API, take negotiation actions
        await this.handleSessions(await this.getSessions());
    }

    private async handleSessions(sessions: IRtcSession[]) {
        await this.syncConnectionsAndSessions(sessions);

        const pairs = sessions.map(session => {
            return {
                session,
                connection: this.connections.find(
                    _ => _.getSessionId() === session.id,
                ),
            };
        });

        await Promise.all(
            pairs.map(
                async ({ session, connection }) =>
                    await this.maintainConnection(session, connection),
            ),
        );
    }

    private async syncConnectionsAndSessions(sessions: IRtcSession[]) {
        const sessionIds = new Set(sessions.map(_ => _.id));

        // Tracked connections have a corresponding session in the signaling API
        const trackedConnection = (_: RtcConnectionV1) =>
            sessionIds.has(_.getSessionId()) ||
            this.connectingSessions.has(_.getSessionId());

        this.connections = this.connections.filter(_ => trackedConnection(_));

        // Mark connections to close which are not tracked,
        // or are in an unhealthy state, but haven't been closed yet.
        const connectionsToClose = this.connections.filter(_ => {
            return !trackedConnection(_) || _.needsClosing();
        });

        await Promise.all(
            connectionsToClose.map(async _ => {
                await this.closeConnection(_);
            }),
        );
    }

    private async maintainConnection(
        session: IRtcSession,
        connection: RtcConnectionV1 | undefined,
    ) {
        const localPeer = await this.getLocalPeer();
        const isOffer = session.offer.peer.id === localPeer.id;

        // answer sessions with currently non-existing connections
        if (!connection) {
            if (isOffer) {
                // offer peers will call connectToSession in "connect"
                return;
            }
            // this is where answer peers call connectToSession
            await this.connectToSession(session);
            return;
        }

        // refresh ready connections
        if (connection.isReady()) {
            try {
                await this.config.signalingClient.refreshSession(
                    await this.config.getToken(),
                    session.id,
                );
            } catch (error) {
                narrowError(error, ResponseError, _ => _.statusCode === 404);
                logger.debug("Refresh session not found", {
                    sessionId: session.id,
                });
            }
            return;
        }

        // consume signals from the sessions of active but not-yet-connected connections
        if (connection.isActive()) {
            try {
                await this.consumeSignals(connection);
            } catch (error) {
                logger.warn("Failed to ingest signals, closing connection", {
                    error,
                });
                await this.closeConnection(connection);
            }
        }

        // do nothing with inactive connections
    }

    private async connectToSession(session: IRtcSession) {
        const localPeer = await this.getLocalPeer();
        const isOffer = session.offer.peer.id === localPeer.id;
        const peerConnection = await this.createRTCPeerConnection();

        const connection = new RtcConnectionV1(peerConnection, {
            localPeer: isOffer ? session.offer.peer : session.answer.peer,
            remotePeer: isOffer ? session.answer.peer : session.offer.peer,
            sessionId: session.id,
            isOffer,
            sendSignal: (signal: IRtcSignal) =>
                this.sendSignal(session.id, signal),
            receive: (peerId: Uuid, message: IRtcStreamMessage) =>
                this.config.receive(peerId, message),
        });

        if (this.connections.find(_ => _.getSessionId() === session.id)) {
            logger.warn(
                "connectToSession called on a session that already has a connection.",
                {
                    sessionId: session.id,
                },
            );
            await connection.close();
            return;
        }

        this.setupHandlers(connection);
        this.connections.push(connection);

        try {
            await connection.connect();
        } catch (error) {
            logger.warn("Failed to handle negotiation, closing connection", {
                error,
            });
            await this.closeConnection(connection);
        }
    }

    private setupHandlers(connection: RtcConnectionV1) {
        const { peerConnection } = connection;
        const sessionId = connection.getSessionId();

        peerConnection.onconnectionstatechange = async () => {
            const connectionTime = this.connectingSessions.get(sessionId);

            if (connectionTime === undefined) {
                // connection already processed
                return;
            }

            const connectionState = peerConnection.connectionState;

            if (isInactive(peerConnection)) {
                this.connectingSessions.delete(sessionId);
            }

            // update rtc kpi metrics
            switch (connectionState) {
                case "connected": {
                    this.connectingSessions.delete(sessionId);
                    const connectDelayMs =
                        new Date().getTime() - connectionTime;
                    stats.aggregate("rtc_connect_time", connectDelayMs);
                    break;
                }
                case "failed":
                    stats.increment("rtc_connect_failed");
                    await this.closeConnection(connection);
                    break;
                default:
                    break;
            }
        };
    }

    private async consumeSignals(connection: RtcConnectionV1) {
        let signals: IRtcSignal[] = [];
        try {
            signals = await this.config.signalingClient.takeSignals(
                await this.config.getToken(),
                connection.getSessionId(),
            );
        } catch (error) {
            narrowError(error, ResponseError, _ => _.statusCode === 404);
        }

        // Signals must be ingested in order.
        for (const signal of signals) {
            await connection.handleSignal(signal);
        }
    }

    private async sendSignal(sessionId: Uuid, signal: IRtcSignal) {
        try {
            await this.config.signalingClient.addSignals(
                await this.config.getToken(),
                sessionId,
                { signals: [signal] },
            );
        } catch (error) {
            narrowError(error, ResponseError, _ => _.statusCode === 404);
        }
    }
}
